import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount, formatPesos, formatPesosCompact, formatRate, parsePesos } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { grantSampleWords } from './promo-words.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { gamingDate } from '@dayon/contracts';

/* ── The shapes the two §12 routes actually return ─────────────────────────
   snake_case, straight off the SELECT list in services/api/src/modules/
   backoffice/catalogue.ts. Every BIGINT is cast `::text` there and becomes a
   BigInt here before it is formatted. */

interface PromotionRow {
  id: string;
  /** PRM-#### — the platform-wide public reference. */
  ref: string;
  /**
   * PR-WKCB. Stable across versions; (key, version) is the promotion's real
   * identity. The design treats this as THE display code, so this screen shows
   * `key` on the card and keeps `ref` for the drawer — the schema offers both
   * and the two must not be used interchangeably.
   */
  key: string;
  version: number;
  name: string;
  /** DEPOSIT_MATCH · CASHBACK · FREE_SPINS · NO_DEPOSIT */
  kind: string;
  /** DRAFT · SCHEDULED · LIVE · PAUSED · ENDED · CANCELLED */
  status: string;
  /**
   * Basis points: 2000 is a 20× requirement.
   *
   * Nullable, because the column is: a DRAFT is allowed to be incomplete, and
   * a version whose multiplier is still unset is precisely the one POST
   * /api/promotions/:id/publish refuses. Null means UNDECIDED, never "this
   * promotion has no wagering requirement". Typing it as a plain string made every
   * unfinished draft render "0× bonus", which is a promise, not a blank.
   */
  wagering_multiplier: string | null;
  max_bonus: string | null;
  /** Untyped bag. The seed writes { window, optIn } and nothing else. */
  eligibility: { window?: string; optIn?: string } | null;
  starts_at: string | null;
  ends_at: string | null;
  /**
   * The three columns the platform has always written and no read returned.
   *
   * `brand_id` is null for a PLATFORM-WIDE campaign — the majority of them —
   * which is a different thing from a brand whose name could not be resolved,
   * so both the id and the resolved name come down. `published_by` is the
   * publisher's display name, null once that admin leaves the roster; the
   * promotion stays published, so the row is not blanked, it says so.
   */
  brand_id: string | null;
  brand: string | null;
  published_by: string | null;
  published_at: string | null;
  players: string;
  granted: string;
  forfeited: string;
  /** granted − forfeited, computed per request and never stored. */
  cost: string;
}

interface GrantRow {
  id: string;
  ref: string;
  player: string;
  player_ref: string;
  promotion: string;
  amount: string;
  wagering_required: string;
  wagering_completed: string;
  progress_bp: string | null;
  /** ACTIVE · COMPLETED · FORFEITED · EXPIRED */
  status: string;
  expires_at: string | null;
}

/**
 * promotion.kind carries four values. The design's type list carries five —
 * "Fixed bonus" and "Referral" have no enum member, so the seed files both of
 * them under NO_DEPOSIT. Labelling NO_DEPOSIT as "Fixed bonus" here would
 * invent a distinction the database cannot make, so it keeps its own name.
 */
const KIND_LABEL: Record<string, string> = {
  DEPOSIT_MATCH: 'Deposit match',
  CASHBACK: 'Cashback',
  FREE_SPINS: 'Free spins',
  NO_DEPOSIT: 'No deposit',
};

/** Live reads as present, scheduled as pending, everything past as quiet. */
const STATUS_TONE: Record<string, 'accent' | 'outline' | 'quiet' | 'neutral'> = {
  LIVE: 'accent',
  SCHEDULED: 'outline',
  PAUSED: 'outline',
  DRAFT: 'quiet',
  ENDED: 'quiet',
  CANCELLED: 'quiet',
};

const STATUS_LABEL: Record<string, string> = {
  LIVE: 'Live',
  SCHEDULED: 'Scheduled',
  PAUSED: 'Paused',
  DRAFT: 'Draft',
  ENDED: 'Ended',
  CANCELLED: 'Cancelled',
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status.toLowerCase();
}

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind.toLowerCase().replace(/_/g, ' ');
}

/**
 * "Wagering multiplies the bonus AMOUNT unless the type is Free spins, where it
 * multiplies WINNINGS." Getting this wrong silently changes what a player owes
 * the operator in turnover, so the noun is derived from the kind every time
 * rather than written into the copy.
 *
 * A NULL MULTIPLIER MEANS NOBODY HAS DECIDED YET — not that this promotion is
 * free of wagering. The two readings are one word apart and produce opposite
 * money, and this label used to give the wrong one: an operator reading "no
 * wagering terms" concluded the promotion was configured and closed the
 * drawer, when the screen was in fact showing them the gap they were the one
 * person placed to close.
 */
function wageringLabel(p: PromotionRow): string {
  if (p.wagering_multiplier === null) return 'wagering not set';
  const times = Number(p.wagering_multiplier) / 100;
  return `${times % 1 === 0 ? times : times.toFixed(1)}× ${p.kind === 'FREE_SPINS' ? 'winnings' : 'bonus'}`;
}

/** Auto-applied promotions never ask; opt-in ones do. Stored only in the jsonb. */
function optInLabel(p: PromotionRow): string {
  const mode = p.eligibility?.optIn;
  if (mode === 'AUTO') return 'Auto';
  if (mode === 'OPT_IN') return 'Opt-in';
  return 'opt-in mode not recorded';
}

/** The human window string is jsonb too; the timestamps are the fallback. */
function windowLabel(p: PromotionRow): string {
  if (p.eligibility?.window) return p.eligibility.window;
  const from = p.starts_at ? gamingDate(p.starts_at) : null;
  const to = p.ends_at ? gamingDate(p.ends_at) : null;
  if (from && to) return `${from} – ${to}`;
  if (from) return `from ${from}`;
  return 'window not recorded';
}

function shortDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-PH', { day: '2-digit', month: 'short' }) : '—';
}

/* ── The lifecycle, as the four §12 write routes model it ──────────────────
   POST /api/promotions/drafts        a new version, unpublished
   POST /api/promotions/:id/publish   DRAFT  → LIVE or SCHEDULED
   POST /api/promotions/:id/pause     LIVE   → PAUSED
   POST /api/promotions/:id/resume    PAUSED → LIVE
   All four require promo.publish. Every reason below is a member of the zod
   enum the route parses, so a code this screen offers is a code the platform
   accepts — the two cannot drift without the build failing. */

interface ReasonOption {
  code: string;
  label: string;
}

const PUBLISH_REASONS: ReasonOption[] = [
  { code: 'NEW_CAMPAIGN', label: 'New campaign' },
  { code: 'TERMS_CORRECTION', label: 'Terms correction' },
  { code: 'WAGERING_CHANGE', label: 'Wagering change' },
  { code: 'BUDGET_CHANGE', label: 'Budget change' },
  { code: 'WINDOW_CHANGE', label: 'Window change' },
  { code: 'REGULATORY_REQUIREMENT', label: 'Regulatory requirement' },
];

const PAUSE_REASONS: ReasonOption[] = [
  { code: 'BUDGET_EXHAUSTED', label: 'Budget exhausted' },
  { code: 'ABUSE_SUSPECTED', label: 'Abuse suspected' },
  { code: 'TERMS_INCORRECT', label: 'Terms incorrect' },
  { code: 'PROVIDER_OUTAGE', label: 'Provider outage' },
  { code: 'REGULATORY_HOLD', label: 'Regulatory hold' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
];

const RESUME_REASONS: ReasonOption[] = [
  { code: 'BUDGET_RESTORED', label: 'Budget restored' },
  { code: 'ABUSE_REVIEW_CLEARED', label: 'Abuse review cleared' },
  { code: 'TERMS_CORRECTED', label: 'Terms corrected' },
  { code: 'PROVIDER_RESTORED', label: 'Provider restored' },
  { code: 'REGULATORY_HOLD_LIFTED', label: 'Regulatory hold lifted' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
];

/**
 * Why a schedule was called off before it ever opened.
 *
 * Matches `promotionCancelReason` in @dayon/contracts exactly. SUPERSEDED is
 * the common one — a replacement version was published and this schedule is no
 * longer wanted; SCHEDULED_IN_ERROR is the honest code for the day somebody
 * publishes the wrong version, and it is worth counting separately from a
 * commercial change of mind.
 */
const CANCEL_REASONS: ReasonOption[] = [
  { code: 'SUPERSEDED', label: 'Superseded by another version' },
  { code: 'SCHEDULED_IN_ERROR', label: 'Scheduled in error' },
  { code: 'TERMS_INCORRECT', label: 'Terms incorrect' },
  { code: 'REGULATORY_HOLD', label: 'Regulatory hold' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
];

/**
 * Why a bonus was granted by hand, and why one was taken back.
 *
 * Closed sets, matching `bonusGrantReason` and `bonusForfeitReason` in
 * @dayon/contracts exactly — the route parses against those, so an option this
 * screen invented would be refused with a field error the operator cannot act
 * on. The code is what "what did we spend on bonuses, and on what grounds"
 * groups by; the note beside it is what one decision reads like a year later.
 */
const GRANT_REASONS: ReasonOption[] = [
  { code: 'CAMPAIGN_ENTITLEMENT', label: 'Qualified for the campaign' },
  { code: 'SERVICE_RECOVERY', label: 'Service recovery' },
  { code: 'RETENTION_OFFER', label: 'Retention offer' },
  { code: 'VIP_HOST_AWARD', label: 'VIP host award' },
  { code: 'MANUAL_CORRECTION', label: 'Manual correction' },
];

const FORFEIT_REASONS: ReasonOption[] = [
  { code: 'ABUSE_CONFIRMED', label: 'Bonus abuse confirmed' },
  { code: 'TERMS_BREACH', label: 'Terms breached' },
  { code: 'GRANTED_IN_ERROR', label: 'Granted in error' },
  { code: 'PLAYER_REQUEST', label: 'Player asked for it to be removed' },
  { code: 'ACCOUNT_CLOSED', label: 'Account closed' },
];

/**
 * Which version of a campaign the card and the drawer speak for.
 *
 * NOT simply the highest version. Drafting v2 of a live campaign must not make
 * the card read "Draft" while v1 is still granting bonuses to players — the
 * record in force is the one an operator is deciding about, and the draft
 * waiting behind it belongs in the version history where it can be published.
 * A campaign with nothing in force falls back to its newest version, which is
 * how a brand-new draft and a finished campaign both still get a card.
 */
const IN_FORCE_RANK: Record<string, number> = {
  LIVE: 0,
  PAUSED: 1,
  SCHEDULED: 2,
  DRAFT: 3,
  ENDED: 4,
  CANCELLED: 5,
};

function currentOf(versions: PromotionRow[]): PromotionRow {
  return versions.toSorted(
    (a, b) =>
      (IN_FORCE_RANK[a.status] ?? 9) - (IN_FORCE_RANK[b.status] ?? 9) || b.version - a.version,
  )[0]!;
}

/**
 * "20" and "20.5" become 2000 and 2050 — exactly, and without a float.
 *
 * `wagering_multiplier` is a bigint of basis points, and 20.1× multiplied by an
 * IEEE double is a different turnover requirement from the one that was typed.
 * Returns null when the field is empty (a draft is allowed to be incomplete)
 * and throws when it is not a multiplier at all, which the caller renders
 * inline rather than sending to a route that would only refuse it.
 */
function toBasisPoints(input: string): string | null {
  const cleaned = input.replace(/[×x\s,]/g, '');
  if (cleaned === '') return null;
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(cleaned)) {
    throw new RangeError('Give the multiplier as a number, e.g. 20 or 20.5.');
  }
  const [whole = '0', fraction = ''] = cleaned.split('.');
  return (BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'))).toString();
}

/** The verbs a version can be given, and the state each one starts from. */
type LifecycleAction = 'publish' | 'pause' | 'resume' | 'cancel' | 'duplicate';

const ACTION_FOR: Record<string, LifecycleAction | undefined> = {
  DRAFT: 'publish',
  SCHEDULED: 'cancel',
  LIVE: 'pause',
  PAUSED: 'resume',
};

/** Short, for the version row, which already names the version beside it. */
const ACTION_LABEL: Record<LifecycleAction, string> = {
  publish: 'Publish',
  pause: 'Pause',
  resume: 'Resume',
  cancel: 'Cancel schedule',
  duplicate: 'Duplicate as draft',
};

/** Long, for the drawer's primary control, which stands alone. */
const PRIMARY_LABEL: Record<LifecycleAction, string> = {
  publish: 'Publish promotion',
  pause: 'Pause promotion',
  resume: 'Resume promotion',
  cancel: 'Cancel promotion',
  duplicate: 'Duplicate as draft',
};

/**
 * What the failure modal says was NOT done.
 *
 * Spelled out rather than built from the verb: "cancel" + "d" reads "canceld",
 * and a misspelling in the one sentence an operator reads when a write failed
 * is the sentence they are least willing to trust.
 */
const ACTION_UNDONE: Record<LifecycleAction, string> = {
  publish: 'published',
  pause: 'paused',
  resume: 'resumed',
  cancel: 'cancelled',
  duplicate: 'duplicated',
};

/* ── Player bonus management ───────────────────────────────────────────── */

function grantColumns(options: {
  canGrant: boolean;
  saving: boolean;
  onForfeit: (grant: GrantRow) => void;
}): Column<GrantRow>[] {
  return [
  {
    key: 'player',
    header: 'Player',
    render: (g) => (
      <>
        {g.player}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {g.player_ref}
        </div>
      </>
    ),
  },
  {
    key: 'promotion',
    header: 'Promotion',
    render: (g) => <span className="text-muted">{g.promotion}</span>,
  },
  {
    key: 'granted',
    header: 'Granted',
    numeric: true,
    render: (g) => <span className="tabular">{formatPesos(BigInt(g.amount))}</span>,
  },
  {
    key: 'progress',
    header: 'Wagering progress',
    render: (g) => {
      if (g.status === 'COMPLETED') return <Tag tone="accent">Completed</Tag>;
      if (g.progress_bp === null) {
        return <span className="text-subtle">no turnover required</span>;
      }
      const bp = Number(g.progress_bp);
      return (
        <>
          <div className="meter-row">
            <span className="tabular">{formatRate(bp)}</span>
            <span className="text-subtle" style={{ fontSize: '11px' }}>
              of {formatPesos(BigInt(g.wagering_required))}
            </span>
          </div>
          {/* Bar plus the number: the bar alone is colour, which is not a value. */}
          <div
            className="meter"
            role="img"
            aria-label={`${formatRate(bp)} of the wagering requirement completed`}
          >
            <div className="meter-fill" style={{ width: `${Math.min(100, bp / 100)}%` }} />
          </div>
        </>
      );
    },
  },
  {
    key: 'status',
    header: 'State',
    secondary: true,
    render: (g) => (
      <Tag tone={g.status === 'ACTIVE' ? 'outline' : 'quiet'}>{g.status.toLowerCase()}</Tag>
    ),
  },
  {
    key: 'expires',
    header: 'Expires',
    secondary: true,
    render: (g) =>
      g.expires_at ? (
        <span className="text-muted">{shortDate(g.expires_at)}</span>
      ) : (
        <span className="text-subtle">never</span>
      ),
  },
  {
    key: 'forfeit',
    header: 'Action',
    render: (g) =>
      /*
       * Only an ACTIVE grant can be taken back, and a closed one says WHY there
       * is no button rather than rendering a disabled control with no reason
       * beside it. A COMPLETED bonus is the player's cash — taking that back is
       * a manual adjustment against the account, which is a different screen and
       * a different capability.
       */
      g.status !== 'ACTIVE' ? (
        <span className="text-subtle">
          {g.status === 'COMPLETED' ? 'converted to cash' : `already ${g.status.toLowerCase()}`}
        </span>
      ) : options.canGrant ? (
        <Button small disabled={options.saving} onClick={() => options.onForfeit(g)}>
          Forfeit
        </Button>
      ) : (
        <span className="text-subtle">needs promo.grant</span>
      ),
  },
  ];
}

/* ── Version history ───────────────────────────────────────────────────────
   Rebuilt client-side by grouping the response on `key`: because publishing
   always writes a new row rather than editing the live one, every version of a
   promotion is already in the list.

   THIS COMMENT USED TO SAY the route returns no publisher and no publish time,
   and it does return both — the version query selects them, the row type
   carries them, and the drawer renders them. The sentence was true when written
   and nothing re-read it when the query changed, so the "Published" column went
   on printing "not returned" over data sitting in the object the row was built
   from. The column shows it now.

   What is still genuinely absent is a CHANGE NOTE: there is no such column on a
   promotion, so a version cannot say why it was published, and that cell is
   named as missing rather than filled in. The design's "Wagering 25× → 20×"
   diff has no source either — it would have to be computed against the previous
   version, which is a feature and not a field. */

function versionColumns(options: {
  canPublish: boolean;
  saving: boolean;
  onAct: (version: PromotionRow, action: LifecycleAction) => void;
}): Column<PromotionRow>[] {
  return [
    {
      key: 'version',
      header: 'Version',
      render: (v) => (
        <span className="row" style={{ gap: 'var(--space-2)' }}>
          <span className="tabular">v{v.version}</span>
          <Tag tone={STATUS_TONE[v.status] ?? 'neutral'}>{statusLabel(v.status)}</Tag>
        </span>
      ),
    },
    {
      key: 'terms',
      header: 'Terms at this version',
      render: (v) => (
        <span className="text-muted">
          {v.wagering_multiplier === null ? (
            <span className="text-subtle">wagering not set — not publishable</span>
          ) : (
            wageringLabel(v)
          )}{' '}
          · {v.max_bonus === null ? 'no cap' : `max ${formatPesos(BigInt(v.max_bonus))}`}
        </span>
      ),
    },
    {
      key: 'note',
      header: 'Change note',
      secondary: true,
      render: () => <span className="text-subtle">not stored</span>,
    },
    {
      key: 'when',
      header: 'Published',
      secondary: true,
      /*
       * THIS CELL SAID "not returned" AND THE DATA WAS RIGHT THERE.
       *
       * It was true when written and stopped being true without anything
       * failing: the version query now selects `published_by, published_at`,
       * the row type carries both, and the drawer two panels away renders them
       * in full. So the one place an operator compares versions side by side
       * told them the platform does not know who published which — while the
       * answer sat in the same object the row was built from.
       *
       * Found by an agent sweeping this file for column names, which is the
       * second stale "we do not have that" claim this pass has turned up. Both
       * were a sentence nobody re-read after the thing it described changed.
       *
       * The two empty cases are the drawer's, word for word rather than
       * paraphrased, so the two places cannot drift into saying different
       * things about the same version.
       */
      render: (v) =>
        v.published_at === null ? (
          <span className="text-subtle">never published</span>
        ) : (
          <span className="text-subtle">
            {new Date(v.published_at).toLocaleDateString('en-PH', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
            {' · '}
            {v.published_by ?? 'an admin no longer on the roster'}
          </span>
        ),
    },
    /*
     * The lifecycle acts on ONE version, so the control lives on the version.
     * The drawer's own button speaks for the record in force; a draft waiting
     * behind a live version is reachable only here, and publishing it from a
     * row that names its number is what keeps "which one am I publishing?"
     * from being a guess.
     */
    {
      key: 'action',
      header: 'Action',
      render: (v) => {
        const action = ACTION_FOR[v.status];
        if (!action || !options.canPublish) return <span className="text-subtle">—</span>;
        return (
          <Button small disabled={options.saving} onClick={() => options.onAct(v, action)}>
            {ACTION_LABEL[action]}
          </Button>
        );
      },
    },
  ];
}

/* ── When a promotion runs ─────────────────────────────────────────────────
   `promo.promotion.starts_at` and `ends_at` have existed since the schema was
   written, `publishPromotionRequest` has accepted both since the write routes
   were built, and the publish route already computes SCHEDULED from a start
   that is still ahead. Nothing ever SENT them. The consequence was not
   cosmetic: every promotion published from this console went LIVE at the moment
   of the click and ran for ever, and the SCHEDULED status the board draws a
   column for could not be reached from the product at all.

   These two turn a chosen DAY into the instant that day begins and ends.

   THE DAY IS THE GAMING DAY — Asia/Manila midnight, which is 16:00Z. A
   promotion that opened at UTC midnight would start at 08:00 Manila, in the
   middle of a playing day, and every figure measured against it (grants,
   cost, the daily rollups keyed on `reporting.daily_metric.day`) would
   straddle two of them. `+08:00` is written into the parsed string rather than
   applied as an offset because the Philippines has no daylight saving and
   never has, so the offset is a constant rather than a date-dependent lookup.

   The END is INCLUSIVE of the day chosen: "runs 5–12 September" means the
   whole of the 12th, so the closing instant is Manila midnight at the END of
   it. Off by one here would be a promotion that stopped granting a day early,
   which is a player complaint rather than a rounding error. */
const MANILA_DAY_MS = 86_400_000;

/** The instant the given Manila day BEGINS, as an ISO instant. */
const dayOpensAt = (isoDay: string): string =>
  new Date(`${isoDay}T00:00:00+08:00`).toISOString();

/** The instant the given Manila day ENDS — midnight at the close of it. */
const dayClosesAt = (isoDay: string): string =>
  new Date(new Date(`${isoDay}T00:00:00+08:00`).getTime() + MANILA_DAY_MS).toISOString();

/** A stored instant back as the Manila day an operator would name. */
const manilaDay = (iso: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date(iso));

/* ── The New promotion wizard ──────────────────────────────────────────── */

interface Draft {
  promoName: string;
  promoType: string;
  matchPct: string;
  maxBonus: string;
  minDeposit: string;
  segment: string;
  brands: string;
  optIn: string;
  excludedAgents: string;
  promoWager: string;
  windowDays: string;
  weighting: string;
  maxBet: string;
  maxWin: string;
  /**
   * Why this campaign exists.
   *
   * Required, and not decoration: `promo.promotion` has no change-note column,
   * so this is the only place the reason for a version is ever written down —
   * it rides with the audit row the draft route writes, which is what the
   * version history points at.
   */
  changeNote: string;
}

/** The Brands box's "every brand" choice, which leaves the promotion's brand unset. */
const ALL_BRANDS = 'All brands';

const EMPTY_DRAFT: Draft = {
  promoName: '',
  promoType: 'Deposit match',
  matchPct: '',
  maxBonus: '',
  minDeposit: '',
  segment: 'New depositors (no prior deposit)',
  brands: ALL_BRANDS,
  optIn: 'Opt-in',
  excludedAgents: '',
  promoWager: '20',
  windowDays: '',
  weighting: 'Slots 100% · Live casino 10% · Table 5%',
  maxBet: '',
  maxWin: '',
  changeNote: '',
};

const WIZARD_STEPS = ['Type & value', 'Eligibility', 'Wagering', 'Review'] as const;

/**
 * The wizard's five type labels against the four `promotion.kind` holds.
 *
 * "Fixed bonus" and "Referral" have no enum member. They are filed under
 * NO_DEPOSIT — which is exactly what the seed does with the two campaigns that
 * are one and the other — rather than refused, and the review step says so in
 * words so nobody discovers the substitution by reading the card afterwards.
 */
const WIZARD_KIND: Record<string, string> = {
  'Deposit match': 'DEPOSIT_MATCH',
  Cashback: 'CASHBACK',
  'Free spins': 'FREE_SPINS',
  'Fixed bonus': 'NO_DEPOSIT',
  Referral: 'NO_DEPOSIT',
};

/**
 * Promotions & bonuses.
 *
 * A campaign console rather than a queue: one card per promotion, opened into a
 * drawer that shows the rules and the version trail.
 *
 * The lifecycle is live: a draft is written, published, paused and resumed
 * through four routes that all require `promo.publish`. What the screen must
 * never do is claim a state change the platform was not asked to make, so every
 * control here either calls its route or is drawn disabled beside the reason —
 * and after an accepted write the whole list is RE-READ rather than the row
 * patched, because a verdict moves the tiles, the live count and the version
 * trail as well as the row it was given on.
 *
 * Failures open the error dialog; only accepted writes raise a toast.
 */
export function Promotions() {
  const { brandId, brands, session, allowed } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  /*
   * The campaigns and the bonus grants are both read behind players.view. A role without it —
   * the PAGCOR Regulator — is refused below, and the reading effect asks this first: the refusal
   * is a render, and an effect registered above it would still fetch on mount.
   */
  const canReadPlayers = allowed('players.view');

  const [promotions, setPromotions] = useState<PromotionRow[]>([]);
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [loading, setLoading] = useState(true);
  /*
   * Did each feed FAIL, as against answering with nothing?
   *
   * Those are opposite instructions to whoever is reading. "promo.promotion is
   * empty" means there is no campaign to pause and none to budget for; "the
   * read did not complete" means the campaigns are unknown and this screen is
   * evidence of nothing. Without the distinction the grid fell through to the
   * first sentence after a request that never returned, so an operator who
   * dismissed the error modal was told in writing that the empty grid was a
   * real zero read live from the table.
   *
   * One flag per read rather than one for the screen: the campaign grid and the
   * bonus grants are two separate requests and either can fail on its own, so a
   * single shared flag would brand a feed that answered perfectly well as
   * broken — and the four tiles above are fed from both.
   */
  const [promotionsFailed, setPromotionsFailed] = useState(false);
  const [grantsFailed, setGrantsFailed] = useState(false);

  // The drawer is local state keyed on the promotion key, not a route: the
  // module is hash-routed at #/promotions and the drawer previews a card.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [bonusesOpen, setBonusesOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0); // 0 = closed, 1..4 = the steps
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  /* ── The two §12 money writes ─────────────────────────────────────────────
     A grant is taken against a VERSION and a forfeit against a GRANT, so they
     are two separate pieces of state rather than one "acting" union with the
     lifecycle: the lifecycle dialog decides a campaign, and these decide a
     player's money. */
  const [granting, setGranting] = useState<PromotionRow | null>(null);
  const [grantPlayerRef, setGrantPlayerRef] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantExpiryDays, setGrantExpiryDays] = useState('');
  const [grantReason, setGrantReason] = useState<string>(GRANT_REASONS[0]!.code);
  const [forfeiting, setForfeiting] = useState<GrantRow | null>(null);
  const [forfeitReason, setForfeitReason] = useState<string>(FORFEIT_REASONS[0]!.code);

  /* ── Writing ──────────────────────────────────────────────────────────────
     Bumped after every accepted write so both feeds re-read. Patching the row
     in place would leave the four tiles, the live count and the version trail
     describing the board as it was — and publishing v2 changes v1's status as
     well as writing v2, so there is no single row to patch. */
  const [writeCount, setWriteCount] = useState(0);
  const [saving, setSaving] = useState(false);
  /** The version a decision is being taken on, and which decision it is. */
  const [acting, setActing] = useState<{ row: PromotionRow; action: LifecycleAction } | null>(null);
  const [reasonCode, setReasonCode] = useState<string>(PUBLISH_REASONS[0]!.code);
  const [note, setNote] = useState('');
  /* The window a publish carries. Both blank means "now, open-ended", which is
     what every publish did before this existed — but now it is a choice
     somebody made rather than the only thing the screen could express. */
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!canReadPlayers) return;
    let cancelled = false;
    setLoading(true);
    // brandId is now READ by §12's handler. It used to ride along ignored, and
    // the notice below said so out loud rather than let the subheader imply a
    // filter that was not applied; the route filters on promotion.brand_id and
    // always keeps the platform-wide rows, so the notice now describes that.
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const suffix = params.toString() ? `?${params}` : '';

    /* allSettled, not all: these are two independent endpoints and each surface
       below has to state its OWN outcome. Under a shared catch a dead grants
       feed also blanked the campaigns that had already arrived, and neither
       empty state could tell "nothing is configured" from "nothing was read". */
    void Promise.allSettled([
      api.get<{ items: PromotionRow[] }>(`/api/promotions${suffix}`),
      api.get<{ items: GrantRow[] }>(`/api/promotions/grants${suffix}`),
    ]).then(([p, g]) => {
      if (cancelled) return;

      // Rows are replaced on success and CLEARED on failure: a list left over
      // from the previous scope, sitting under a failure notice, would read as
      // the current one.
      setPromotions(p.status === 'fulfilled' ? p.value.items : []);
      setPromotionsFailed(p.status === 'rejected');
      setGrants(g.status === 'fulfilled' ? g.value.items : []);
      setGrantsFailed(g.status === 'rejected');
      setLoading(false);

      // One dialog, naming which feed actually failed rather than "something".
      const failures = [
        p.status === 'rejected' ? { what: 'promotions', reason: p.reason as unknown } : null,
        g.status === 'rejected' ? { what: 'grants', reason: g.reason as unknown } : null,
      ].filter((f) => f !== null);
      if (failures.length === 0) return;

      const first = failures[0]!.reason;
      showError({
        title:
          failures.length === 2
            ? 'Could not load promotions or bonus grants'
            : failures[0]!.what === 'promotions'
              ? 'Could not load promotions'
              : 'Could not load bonus grants',
        message:
          first instanceof ApiRequestError
            ? first.message
            : 'The read did not complete. Try again shortly.',
        ...(first instanceof ApiRequestError ? { code: first.code } : {}),
      });
    });

    return () => {
      cancelled = true;
    };
  }, [canReadPlayers, brandId, showError, writeCount]);

  /** Every version of every promotion, newest version first, grouped on `key`. */
  const byKey = useMemo(() => {
    const map = new Map<string, PromotionRow[]>();
    for (const p of promotions) {
      const bucket = map.get(p.key);
      if (bucket) bucket.push(p);
      else map.set(p.key, [p]);
    }
    for (const bucket of map.values()) bucket.sort((a, b) => b.version - a.version);
    return map;
  }, [promotions]);

  /** One card per promotion — the version IN FORCE is the current record. */
  const current = useMemo(() => [...byKey.values()].map(currentOf), [byKey]);

  const liveCount = current.filter((p) => p.status === 'LIVE').length;

  /* Bonus economics, from the two feeds and nothing else. */
  const totals = useMemo(() => {
    let cost = 0n;
    let granted = 0n;
    let forfeited = 0n;
    for (const p of current) {
      cost += BigInt(p.cost);
      granted += BigInt(p.granted);
      forfeited += BigInt(p.forfeited);
    }
    // The grants feed is the 100 most recent, so anything derived from it is a
    // sample of the tail, and the tile labels say so.
    const activePlayers = new Set(
      grants.filter((g) => g.status === 'ACTIVE').map((g) => g.player_ref),
    ).size;
    let required = 0n;
    let completed = 0n;
    for (const g of grants) {
      required += BigInt(g.wagering_required);
      completed += BigInt(g.wagering_completed);
    }
    const completionBp = required === 0n ? null : Number((completed * 10_000n) / required);
    return { cost, granted, forfeited, activePlayers, completionBp };
  }, [current, grants]);

  const selectedVersions = openKey ? (byKey.get(openKey) ?? []) : [];
  /* The drawer speaks for the version IN FORCE, not the newest row — the same
     record the card shows, so opening a card never changes what it says. */
  const selected = selectedVersions.length > 0 ? currentOf(selectedVersions) : null;

  // Only rendered when a scope is actually set — see the notice below.
  const brandLabel = brands.find((b) => b.id === brandId)?.name ?? 'the selected brand';

  // "Publish promotion: Super Admin ✓, everyone else ✗." The wizard shows the
  // verdict before the last step so nobody fills four screens to be refused.
  const canPublish = allowed('promo.publish');
  /*
   * A DIFFERENT capability from the one above, and deliberately so: publishing
   * decides what a campaign offers, granting hands one player money out of the
   * budget. A role could hold either without the other, so the screen asks
   * about each separately rather than treating "may publish" as "may grant".
   */
  const canGrant = allowed('promo.grant');
  const promoNextDisabled = wizardStep === 1 && draft.promoName.trim() === '';

  function openWizard() {
    setDraft(EMPTY_DRAFT);
    setWizardStep(1);
    setFields({});
  }

  /* ── The four writes ──────────────────────────────────────────────────────
     Each opens a dialog first: every one of them carries a reason CODE and a
     note, because a lifecycle move with no recorded why is a campaign that
     changed for reasons nobody can reconstruct. The code is a closed set the
     route parses; the note is what the next operator actually reads. */

  const openAction = useCallback(
    (row: PromotionRow, action: LifecycleAction) => {
      /* Permission first. An operator who may not publish is told so, rather
         than walked into a form whose submit they cannot use. */
      if (!canPublish) {
        showError({
          title: `${row.key} cannot be changed by this role`,
          message:
            `Changing a promotion at all is not something ${session?.role ?? 'your role'} can ` +
            'do. Only a Super Admin can, and ' +
            'a second factor is required to do it.',
        });
        return;
      }
      setActing({ row, action });
      setReasonCode(
        (action === 'pause'
          ? PAUSE_REASONS[0]
          : action === 'resume'
            ? RESUME_REASONS[0]
            : action === 'cancel'
              ? CANCEL_REASONS[0]
              : PUBLISH_REASONS[0])!.code,
      );
      setNote('');
      setFields({});
      /* Seeded from the version's OWN stored window when it has one — a draft
         duplicated from a scheduled promotion carries its dates forward, and
         retyping them is how they drift. */
      setStartsOn(row.starts_at ? manilaDay(row.starts_at) : '');
      setEndsOn(row.ends_at ? manilaDay(row.ends_at) : '');
    },
    [canPublish, session, showError],
  );

  const submitAction = useCallback(async () => {
    if (!acting) return;
    const { row, action } = acting;

    const trimmed = note.trim();
    const problems: Record<string, string> = {};
    if (trimmed.length < 10) {
      problems['note'] = 'Say what prompted this — at least 10 characters.';
    }
    /* Inline, on the field, before the request. The contract refuses a close
       that precedes an open too, but a round trip to be told which of two
       boxes is wrong is a round trip the operator did not need. */
    if (action === 'publish' && startsOn && endsOn && endsOn < startsOn) {
      problems['endsOn'] = 'A promotion cannot close before it opens.';
    }
    if (action === 'publish' && endsOn && !startsOn) {
      problems['startsOn'] =
        'A closing day needs an opening day — otherwise the promotion starts the moment you publish and the end is measured from nothing anybody chose.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    setFields({});
    setSaving(true);
    try {
      if (action === 'duplicate') {
        /* The next version of THIS campaign, carrying the terms as they stand.
           The key is what makes it a version rather than a new campaign, and
           the route computes the number — this screen never guesses it. */
        const made = await api.post<{ key: string; version: number }>('/api/promotions/drafts', {
          key: row.key,
          name: row.name,
          kind: row.kind,
          ...(row.wagering_multiplier !== null
            ? { wageringMultiplier: row.wagering_multiplier }
            : {}),
          ...(row.max_bonus !== null ? { maxBonus: row.max_bonus } : {}),
          optIn: row.eligibility?.optIn === 'AUTO' ? 'AUTO' : 'OPT_IN',
          ...(row.eligibility?.window ? { window: row.eligibility.window } : {}),
          note: trimmed,
        });
        toast(
          `${made.key} v${made.version} saved as a draft. Nothing is live until it is published.`,
        );
      } else {
        const result = await api.post<{
          key: string;
          version: number;
          status: string;
          /* Optional because only publish returns a window. Cancel answers with
             `grantsHeldAtCancellation` instead — the number that makes "nothing
             was clawed back" checkable rather than asserted. */
          startsAt?: string | null;
          endsAt?: string | null;
          grantsHeldAtCancellation?: number;
        }>(`/api/promotions/${row.id}/${action}`, {
          reasonCode,
          note: trimmed,
          /* Only on publish, and only when chosen. Sending `null` would be a
             different statement — "clear whatever this draft already carried" —
             and the route reads an absent field as "keep it". */
          ...(action === 'publish' && startsOn ? { startsAt: dayOpensAt(startsOn) } : {}),
          ...(action === 'publish' && endsOn ? { endsAt: dayClosesAt(endsOn) } : {}),
        });
        toast(
          action === 'publish'
            ? `${result.key} v${result.version} is ${statusLabel(result.status).toLowerCase()}${
                result.startsAt && result.status === 'SCHEDULED'
                  ? ` — it opens on ${manilaDay(result.startsAt)}`
                  : ''
              }${result.endsAt ? `, closing after ${manilaDay(new Date(new Date(result.endsAt).getTime() - 1).toISOString())}` : ''}.`
            : action === 'pause'
              ? `${result.key} v${result.version} is paused — no new grants. Bonuses already held keep running.`
              : action === 'cancel'
                ? `${result.key} v${result.version} is cancelled — it will not open. ${
                    result.grantsHeldAtCancellation
                      ? `${formatCount(result.grantsHeldAtCancellation)} bonuses were already held against it and none were touched — tell Finance.`
                      : 'No bonus had been granted against it, so nothing was taken back.'
                  }`
                : `${result.key} v${result.version} is live again. Nothing expired was revived.`,
        );
      }
      setActing(null);
      setNote('');
      // RE-READ, never patch: publishing moves two rows, and every tile above.
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) setFields(error.fields);
      showError({
        title: `${row.key} v${row.version} was not ${ACTION_UNDONE[action]}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [acting, note, reasonCode, startsOn, endsOn, showError, toast]);

  /* ── Granting a bonus ─────────────────────────────────────────────────────
     The first write on this screen that moves money. It posts a ledger
     transaction PROMO_FUNDING → PLAYER_BONUS in the same transaction as the
     grant row, so there is no state in which one exists without the other —
     which is why the confirmation says what moved and not merely "done". */

  const openGrant = useCallback(
    (row: PromotionRow) => {
      if (!canGrant) {
        showError({
          title: `A bonus cannot be granted by this role`,
          message:
            `Giving a player a bonus is not something ${session?.role ?? 'your role'} can do. ` +
            'It is kept separate from editing a campaign on purpose: deciding what a campaign offers is not the same ' +
            'authority as paying one player out of the budget.',
        });
        return;
      }
      setGranting(row);
      setGrantPlayerRef('');
      /* Seeded with the CAP when the version has one, because that is the
         commonest grant and the only figure the platform can state itself. A
         version with no cap starts blank rather than at a number nobody
         published. */
      setGrantAmount(row.max_bonus === null ? '' : (Number(BigInt(row.max_bonus)) / 100).toString());
      setGrantExpiryDays('');
      setGrantReason(GRANT_REASONS[0]!.code);
      setNote('');
      setFields({});
    },
    [canGrant, session, showError],
  );

  const submitGrant = useCallback(async () => {
    if (!granting) return;
    const row = granting;

    const problems: Record<string, string> = {};
    const ref = grantPlayerRef.trim();
    if (ref === '') problems['playerRef'] = 'Give the player reference, e.g. PL-10241.';

    let amount: bigint | null = null;
    try {
      amount = parsePesos(grantAmount);
      if (amount <= 0n) problems['amount'] = 'A bonus of nothing is not a bonus.';
      else if (row.max_bonus !== null && amount > BigInt(row.max_bonus)) {
        problems['amount'] =
          `${row.key} caps a bonus at ${formatPesos(BigInt(row.max_bonus))}. Publish a new ` +
          'version to raise the cap.';
      }
    } catch {
      problems['amount'] = 'Give the amount in pesos, e.g. 500.';
    }

    let expiresInDays: number | null = null;
    if (grantExpiryDays.trim() !== '') {
      const days = Number(grantExpiryDays);
      if (!Number.isInteger(days) || days < 1 || days > 365) {
        problems['expiry'] = 'Between 1 and 365 days, or leave blank.';
      } else expiresInDays = days;
    }

    const trimmed = note.trim();
    if (trimmed.length < 10) {
      problems['note'] = 'Say why this bonus is being granted — at least 10 characters.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    setFields({});
    setSaving(true);
    try {
      /*
       * THE REFERENCE IS RESOLVED TO AN ID, and an ambiguous or unknown one is
       * refused HERE rather than sent.
       *
       * `PL-10241` is what an operator reads off a ticket; the route takes the
       * id, because a ref is a display code and the grant has to name the row.
       * The search is the roster's own route and is brand-scoped by the session,
       * so a ref on another brand simply does not come back — and the field
       * error says that rather than "not found", which would read as a typo.
       */
      const found = await api.get<{ items: { id: string; ref: string; name: string }[] }>(
        `/api/players?q=${encodeURIComponent(ref)}&perPage=5`,
      );
      const match = found.items.find((p) => p.ref.toLowerCase() === ref.toLowerCase());
      if (!match) {
        setFields({
          playerRef:
            `No player is filed under ${ref} in your brand scope. The reference is the one on ` +
            'the player record, e.g. PL-10241.',
        });
        return;
      }

      const granted = await api.post<{
        ref: string;
        playerRef: string;
        amount: string;
        wageringRequired: string;
        wageringNote: string;
        expiresAt: string | null;
        transactionRef: string;
      }>(`/api/promotions/${row.id}/grants`, {
        playerId: match.id,
        amount: amount!.toString(),
        ...(expiresInDays !== null ? { expiresInDays } : {}),
        reasonCode: grantReason,
        note: trimmed,
      });

      /* What actually happened, in the vocabulary of the ledger. "Bonus
         granted" alone would leave the operator to assume where the money came
         from, and the commonest wrong assumption is the player's own balance. */
      toast(
        `${granted.ref}: ${formatPesos(BigInt(granted.amount))} credited to ${granted.playerRef}'s ` +
          `bonus wallet from the promotion budget (${granted.transactionRef}). ` +
          granted.wageringNote,
      );
      setGranting(null);
      setNote('');
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) setFields(error.fields);
      showError({
        title: `No bonus was granted on ${row.key}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written and no money moved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [granting, grantPlayerRef, grantAmount, grantExpiryDays, grantReason, note, showError, toast]);

  /* ── Taking one back ──────────────────────────────────────────────────────
     The amount returns to the promotion budget and NOT to the player, which the
     notice on this dialog has said since before there was a route. */

  const openForfeit = useCallback((grant: GrantRow) => {
    setForfeiting(grant);
    setForfeitReason(FORFEIT_REASONS[0]!.code);
    setNote('');
    setFields({});
  }, []);

  const submitForfeit = useCallback(async () => {
    if (!forfeiting) return;
    const grant = forfeiting;

    const trimmed = note.trim();
    if (trimmed.length < 10) {
      setFields({ note: 'Say why the bonus is being taken back — at least 10 characters.' });
      return;
    }
    setFields({});
    setSaving(true);
    try {
      const result = await api.post<{ ref: string; amount: string; transactionRef: string }>(
        `/api/promotions/grants/${grant.id}/forfeit`,
        { reasonCode: forfeitReason, note: trimmed },
      );
      toast(
        `${result.ref}: ${formatPesos(BigInt(result.amount))} returned to the promotion budget ` +
          `(${result.transactionRef}). ${grant.player_ref} was not charged and was not paid.`,
      );
      setForfeiting(null);
      setNote('');
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) setFields(error.fields);
      showError({
        title: `${grant.ref} was not forfeited`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written and no money moved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [forfeiting, forfeitReason, note, showError, toast]);

  /**
   * The wizard writes a DRAFT — version 1 of a brand-new campaign.
   *
   * It deliberately does not publish. Publishing needs the wagering terms
   * present and is refused without them, so the draft is where the terms are
   * got right and the publish step is a separate decision taken against a
   * stored version rather than against a form.
   */
  const submitWizard = useCallback(async () => {
    const problems: Record<string, string> = {};
    const name = draft.promoName.trim();
    if (name.length < 3) problems['promoName'] = 'Give the promotion the name players will see.';

    let wageringMultiplier: string | null = null;
    try {
      wageringMultiplier = toBasisPoints(draft.promoWager);
    } catch (error: unknown) {
      problems['promoWager'] =
        error instanceof RangeError ? error.message : 'That is not a multiplier.';
    }

    let maxBonus: string | null = null;
    if (draft.maxBonus.trim() !== '') {
      try {
        const centavos = parsePesos(draft.maxBonus);
        if (centavos <= 0n) problems['maxBonus'] = 'A cap of zero grants nothing. Leave it blank.';
        else maxBonus = centavos.toString();
      } catch {
        problems['maxBonus'] = 'Give the cap in pesos, e.g. 5,000.';
      }
    }

    const trimmed = draft.changeNote.trim();
    if (trimmed.length < 10) {
      problems['changeNote'] = 'Say what this campaign is for — at least 10 characters.';
    }

    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    setFields({});

    const chosenBrand = brands.find((b) => b.name === draft.brands);

    setSaving(true);
    try {
      const made = await api.post<{ key: string; version: number }>('/api/promotions/drafts', {
        name,
        kind: WIZARD_KIND[draft.promoType] ?? 'NO_DEPOSIT',
        ...(wageringMultiplier !== null ? { wageringMultiplier } : {}),
        ...(maxBonus !== null ? { maxBonus } : {}),
        optIn: draft.optIn === 'Automatic' ? 'AUTO' : 'OPT_IN',
        ...(chosenBrand ? { brandId: chosenBrand.id } : {}),
        note: trimmed,
      });
      toast(`${made.key} v${made.version} saved as a draft. Publish it from its card when ready.`);
      setWizardStep(0);
      setDraft(EMPTY_DRAFT);
      setOpenKey(made.key);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) setFields(error.fields);
      showError({
        title: 'The draft was not saved',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [brands, draft, showError, toast]);

  /** The reason list the open dialog offers, matching the route that parses it. */
  const reasonOptions =
    acting?.action === 'pause'
      ? PAUSE_REASONS
      : acting?.action === 'resume'
        ? RESUME_REASONS
        : acting?.action === 'cancel'
          ? CANCEL_REASONS
          : PUBLISH_REASONS;

  // Below every hook, so the hooks run in the same order for every reader; the
  // read above is what `canReadPlayers` stops.
  if (!canReadPlayers) {
    return (
      <EmptyState icon="lock-key" title="Promotions & bonuses">
        This module needs the players.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  return (
    <>
      {/*
        THE SENTENCE UNDER THE NAME IS WHAT THIS HEADER IS FOR.

        This screen opened with "Promotions & bonuses", a count line, four money
        tiles and a board of cards. Every word of it was true and none of it said
        what the screen was FOR, so the person most likely to need telling —
        somebody new to the promotions desk, or sent here once a quarter to pause
        one campaign — had to infer the job from the figures. The explanation was
        already written, at length, in the module comment at the top of this
        file, where only somebody editing the source would ever read it.

        It sits ABOVE the three notices that follow rather than among them. Those
        carry rules about the DATA — which campaigns a brand scope keeps, and
        which figures nothing in the platform produces — and a sentence about the
        SCREEN stacked underneath them would be read as the fourth paragraph of
        one wall of prose and skipped with it, which is precisely the sentence
        that must not be skipped.
      */}
      <ScreenHeader
        title="Promotions & bonuses"
        purpose="The promotions team runs bonus campaigns from here — putting them live, pausing them, and watching what the bonuses cost."
        /*
          The caption moves across unchanged, all three of its states.

          They are not interchangeable. "Loading…" means the answer has not
          arrived yet; "The promotion list could not be read" means the request
          failed, so there is no count to show and none was ever made; and a
          figure means the route answered. Collapsing that middle state into a
          zero would tell an operator that no campaign is configured — which is
          something they would act on, by building one — when what actually
          happened is that nobody managed to ask. The four tiles, the panel's row
          count and the board's two empty states all lean on `promotionsFailed`
          for the same reason, and all four have to agree.

          The loading state is held to `current.length === 0` rather than to
          `loading` alone so that the re-read after an accepted write does not
          blank a count somebody is mid-sentence about.

          NOTE FOR WHOEVER EDITS THIS NEXT, because it is a real defect carried
          across rather than introduced here: the trailing "all brands" is
          WRITTEN DOWN instead of derived, and it is wrong whenever the brand
          picker is set. §12's handler does filter on `promotion.brand_id` and
          keeps the platform-wide rows, which is exactly what the notice directly
          below this header explains — so with Bingo Barrio selected the caption
          claims a breadth the notice beneath it contradicts. That is the same
          class of bug `dashboard-scope-captions.test.ts` was written for, and
          the fix is already sitting above as `brandLabel`. It was NOT quietly
          corrected inside a change about headers: the count line is moved here
          verbatim, and the wording is somebody's next commit, with the matching
          test, rather than an unreviewed edit smuggled into a layout one.
        */
        meta={
          loading && current.length === 0
            ? 'Loading…'
            : promotionsFailed
              ? 'The promotion list could not be read — no count is shown'
              : `${formatCount(current.length)} promotions · ${liveCount} live · all brands`
        }
        /* `header-spacer` came off the first button with the hand-rolled row it
           belonged to: the header's own action slot is pushed right by
           `justify-content: space-between`, so a margin-left:auto inside that
           slot has nothing left to push against and only misleads whoever reads
           it next. */
        actions={
          <>
            <Button onClick={() => setBonusesOpen(true)}>
              <Icon name="gift" />
              Player bonuses
            </Button>
            <Button variant="primary" onClick={openWizard}>
              <Icon name="plus" />
              New promotion
            </Button>
          </>
        }
      />

      {/* The global brand scope IS honoured here now. It was not: the route
          neither selected nor filtered on promotion.brand_id, and this notice
          said so rather than let the subheader imply a filter that was absent.
          Both halves changed together — a list that names a brand has to be
          scoped by one. */}
      {brandId && (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Brand scope is set to <strong>{brandLabel}</strong>, so the board below shows that
            brand&rsquo;s campaigns <em>and</em> every platform-wide one. A promotion with no brand
            on it runs everywhere by definition, so hiding it under a brand filter would be its own
            kind of wrong answer — each card says which of the two it is.
          </div>
        </div>
      )}

      {loading && current.length === 0 ? (
        <div className="stat-grid">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="skeleton" style={{ height: '11px', width: '60%' }}>
                &nbsp;
              </div>
              <div className="skeleton" style={{ height: '25px', width: '80%' }}>
                &nbsp;
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="stat-grid">
          <Stat
            label="Bonus cost to date"
            value={promotionsFailed ? '—' : formatPesosCompact(totals.cost)}
            delta={
              promotionsFailed
                ? 'the promotions read failed — nothing was totalled, and this is not ₱0.00'
                : `${formatPesosCompact(totals.granted)} granted − ${formatPesosCompact(totals.forfeited)} forfeited`
            }
          />
          {/* The design's second tile is "Bonus-funded GGR". reporting.dailyMetric
              holds one bonusCost column per brand-day and there is no promo-level
              rollup, so there is nothing to divide by. An em dash, not a zero. */}
          <Stat
            label="Bonus-funded GGR"
            value="—"
            delta="no per-promotion revenue figure is kept"
          />
          <Stat
            label="Players on an active bonus"
            value={grantsFailed ? '—' : formatCount(totals.activePlayers)}
            delta={
              grantsFailed
                ? 'the grants read failed — nobody was counted, and this is not zero players'
                : grantSampleWords(grants.length)
            }
          />
          <Stat
            label="Wagering completion"
            value={
              grantsFailed || totals.completionBp === null ? '—' : formatRate(totals.completionBp)
            }
            delta={
              grantsFailed
                ? 'the grants read failed — there is no sample to divide'
                : totals.completionBp === null
                  ? 'no grant carries a turnover requirement'
                  : 'turnover completed ÷ required, same sample'
            }
          />
        </div>
      )}

      <div className="notice notice-quiet">
        <Icon name="chart-line" />
        <div>
          Three figures are not shown above because nothing produces them: bonus cost as a share
          of GGR, bonus-funded GGR, and every “vs prior period” comparison. They need a daily
          total kept for each promotion, which does not exist. Bonus cost is granted minus
          forfeited and therefore keeps moving
          after a promotion ends.
        </div>
      </div>

      <Panel
        title="Campaigns"
        actions={
          <span className="text-subtle" style={{ fontSize: '12px' }}>
            {promotionsFailed
              ? 'nothing read'
              : current.length === promotions.length
                ? 'current version of each promotion'
                : `current of ${promotions.length} rows across all versions`}
          </span>
        }
      >
        {loading && current.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : current.length === 0 ? (
          promotionsFailed ? (
            /* The failure branch comes FIRST, because a failed read leaves the
               grid looking exactly like an empty one and only this sentence
               tells them apart. */
            <EmptyState icon="warning-circle" title="The campaign list could not be read">
              The promotions could not be fetched, so this grid is
              empty because nothing was read — not because no promotion is configured. Nothing here
              is evidence that the promotion record is empty. The
              dialog carries the reason and the error code; reload to try the read again.
            </EmptyState>
          ) : (
            <EmptyState icon="gift" title="No promotions configured">
              the promotion record is empty in this environment. This
              is a real zero read live from the table, not a filtered view — there are no
              promotion-local filters on this screen.
            </EmptyState>
          )
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
              gap: 'var(--space-4)',
            }}
          >
            <PromotionGrid rows={current} onOpen={setOpenKey} />
          </div>
        )}
      </Panel>

      {/* ── Detail drawer ──────────────────────────────────────────────── */}
      {selected && (
        <Drawer
          open
          onClose={() => setOpenKey(null)}
          title={selected.name}
          subtitle={
            <>
              {selected.key} · {kindLabel(selected.kind)} · {windowLabel(selected)} ·{' '}
              {selected.ref}
            </>
          }
          actions={
            <>
              {/* Duplicate writes the NEXT version as a draft — the only way a
                  published promotion's terms are ever changed. */}
              <Button
                disabled={!canPublish || saving}
                onClick={() => openAction(selected, 'duplicate')}
              >
                Duplicate as draft
              </Button>
              {/* GRANTING, on a live version only. A paused or ended campaign
                  makes no new grants — the route refuses it server-side and
                  this does not offer the control at all.
                  
                  NOT hidden from a role that cannot grant. The control is
                  rendered and `openGrant` answers with the reason — which
                  capability is required, and which one this role holds — in a
                  modal. A control that vanishes teaches an operator that the
                  screen is broken; one that says why teaches them who to ask. */}
              {selected.status === 'LIVE' ? (
                <Button disabled={saving} onClick={() => openGrant(selected)}>
                  Grant bonus
                </Button>
              ) : null}
              {/* SCHEDULED resolves to `cancel` here exactly as every other
                  state resolves to its own verb. The hard-disabled dead end that
                  used to sit in this branch is gone: POST
                  /api/promotions/:id/cancel exists, so the control works. */}
              {ACTION_FOR[selected.status] && canPublish ? (
                <Button
                  variant="primary"
                  disabled={saving}
                  onClick={() => openAction(selected, ACTION_FOR[selected.status]!)}
                >
                  {PRIMARY_LABEL[ACTION_FOR[selected.status]!]}
                </Button>
              ) : null}
            </>
          }
        >
          <div className="stack">
            <div className="stat-grid">
              <Stat label="Players" value={formatCount(Number(selected.players))} />
              <Stat
                label="Bonus cost"
                value={
                  BigInt(selected.cost) === 0n ? '—' : formatPesosCompact(BigInt(selected.cost))
                }
                delta={
                  BigInt(selected.forfeited) === 0n
                    ? 'nothing forfeited yet'
                    : `${formatPesosCompact(BigInt(selected.forfeited))} forfeited back to budget`
                }
              />
              <Stat label="Effect" value="—" delta="no promo-level GGR series" />
            </div>

            <Panel title="Rules">
              <dl className="stack">
                <div className="def-row">
                  <dt>Eligibility</dt>
                  <dd>
                    {selected.eligibility?.optIn === 'AUTO'
                      ? 'All eligible players, auto-applied'
                      : selected.eligibility?.optIn === 'OPT_IN'
                        ? 'Opt-in required'
                        : 'not recorded'}
                  </dd>
                </div>
                <div className="def-row">
                  <dt>Maximum bonus</dt>
                  <dd>
                    {selected.max_bonus === null ? (
                      <span className="text-subtle">no cap recorded</span>
                    ) : (
                      formatPesos(BigInt(selected.max_bonus))
                    )}
                  </dd>
                </div>
                <div className="def-row">
                  <dt>Wagering</dt>
                  <dd>{wageringLabel(selected)}</dd>
                </div>
                <div className="def-row">
                  <dt>Window</dt>
                  <dd>{windowLabel(selected)}</dd>
                </div>
                <div className="def-row">
                  <dt>Opt-in mode</dt>
                  <dd>{optInLabel(selected)}</dd>
                </div>
                <div className="def-row">
                  <dt>Current version</dt>
                  <dd>
                    v{selected.version} · {statusLabel(selected.status)}
                  </dd>
                </div>
                {/* Written on every promotion since the module was built, and
                    returned by no read until now: a board that says a campaign
                    is live and cannot say who put it there is a board an
                    incident review cannot use. */}
                <div className="def-row">
                  <dt>Brand</dt>
                  <dd>
                    {selected.brand ?? (
                      <>
                        <Tag tone="neutral">Platform-wide</Tag>{' '}
                        <span className="text-subtle">runs on every brand</span>
                      </>
                    )}
                  </dd>
                </div>
                <div className="def-row">
                  <dt>Published</dt>
                  <dd>
                    {selected.published_at ? (
                      <>
                        {new Date(selected.published_at).toLocaleString('en-PH')} ·{' '}
                        {selected.published_by ?? (
                          <span className="text-subtle">
                            by an admin no longer on the roster
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-subtle">
                        never published — this version has only ever been a draft
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </Panel>

            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                Minimum deposit, completion window, game weighting, max bet while wagering, max win
                and excluded agents are not kept on{' '}
                the promotion record or among its eligibility
                rules, so they are omitted rather than guessed.{' '}
                <strong>What IS checked</strong>, on the server, at every grant and every player
                opt-in: that this version is live and inside its window by the platform&rsquo;s own
                clock, that the player is on a brand this promotion covers, that the account is not
                suspended and carries no <em>bonus exclusion</em> or self-exclusion, that they are
                not already holding an active grant from this version, and that the amount is
                within the published cap. The eligibility bag itself holds only the opt-in mode and
                the window label — there is no machine-evaluable rule in it, and nothing here
                pretends otherwise.
              </div>
            </div>

            <Panel title="Version history">
              <DataTable
                caption={`Every stored version of ${selected.key}, newest first`}
                columns={versionColumns({ canPublish, saving, onAct: openAction })}
                rows={selectedVersions}
                rowKey={(v) => v.id}
              />
              {/* THE TWO COLUMN NAMES ARE NOT FOR AN OPERATOR. This sentence
                  printed `published_by` and `published_at` in tabular spans,
                  so they read as deliberate — and they were, to a developer.
                  Somebody asking who put a campaign live cannot look either
                  name up, ask anyone for it, or repeat it to a reviewer. The
                  two greyed cells in the table above say the same thing in
                  words they already have.

                  The reason the sentence gave for them had also gone stale, so
                  it no longer gives it. The list query selects published_by and
                  published_at (services/api/src/modules/promotions/routes.ts)
                  and the Published row in the panel above renders both, so "the
                  route returns neither" stopped being true the day those
                  columns started coming down. What is still true is what it
                  says now: this list shows neither against a version, because
                  versionColumns renders the Change note and Published cells as
                  fixed text rather than from the row. promo.promotion has no
                  change-note column at all, and the note the wizard collects
                  travels on the audit row the publish writes — which is why the
                  audit trail is where both are to be found. */}
              <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                <Icon name="clock-counter-clockwise" />
                <div>
                  {selectedVersions.length === 1
                    ? 'Only v1 exists — a live promotion is never edited in place, so a change would appear here as v2.'
                    : 'Publishing writes a new row; the live one is never edited in place.'}{' '}
                  Neither the change note nor the publisher appears against a version
                  here: a version carries no change note of its own, and this list does
                  not say who published one or when. Both are kept in the audit trail,
                  which this screen does not read.
                </div>
              </div>
            </Panel>

            <div className="notice notice-quiet">
              <Icon name="git-branch" />
              <div>
                {/* ONE IDEA PER SENTENCE, AND NO CAPABILITY KEYS. This was a
                    single block naming promo.publish and promo.grant, which the
                    reader cannot grant themselves, and it ran four controls
                    together in one breath. What each control does to a player
                    holding a bonus is the part that matters, so that is what
                    each sentence is about now. */}
                <strong>Duplicate</strong> starts the next version as a draft. Publishing that
                version retires this one and changes nothing about it, so anyone already given a
                bonus keeps the terms they were given.{' '}
                <strong>Pause</strong> stops new bonuses being given out. Bonuses people already
                hold keep running until they expire on their own. Resuming starts new ones again
                and brings nothing expired back.{' '}
                <strong>Give a bonus</strong> is a separate thing, and not everyone who can edit a
                campaign can do it — it spends the promotion budget rather than deciding what the
                campaign offers.{' '}
                {selected.status === 'SCHEDULED' ? (
                  <>
                    <strong>Cancel</strong> calls this off before it opens, and only while it is
                    still scheduled. Once it is live people may already hold bonuses under it, and
                    pausing is the control for that instead. Nothing is taken back, and the
                    confirmation tells you how many bonuses exist rather than assuming none do.
                  </>
                ) : (
                  <>
                    <strong>Cancel</strong> is offered only while a promotion is still scheduled.
                    It is how you call off something that should not open, rather than publishing a
                    replacement version nobody wanted.
                  </>
                )}
              </div>
            </div>
          </div>
        </Drawer>
      )}

      {/* ── Player bonus management ────────────────────────────────────── */}
      <Dialog
        open={bonusesOpen}
        onClose={() => setBonusesOpen(false)}
        title="Player bonus management"
        wide
        actions={<Button onClick={() => setBonusesOpen(false)}>Close</Button>}
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Forfeiting a bonus returns the amount to the promotion budget, not to the player:{' '}
              it is a ledger movement out of the player&rsquo;s bonus wallet and back
              into the budget that funded it, written in the same transaction as the
              audit row. It can only be done{' '}
              by somebody allowed to give bonuses, and only an{' '}
              <em>active</em> grant can be taken back — once wagering is met the bonus has already
              become the player&rsquo;s cash, and that is a manual adjustment against the account
              rather than a forfeit. The list shows the 100 most recent grants.
            </div>
          </div>
          <DataTable
            caption="Bonus grants with wagering progress, newest first"
            columns={grantColumns({ canGrant, saving, onForfeit: openForfeit })}
            rows={grants}
            rowKey={(g) => g.id}
            empty={
              grantsFailed ? (
                <EmptyState icon="warning-circle" title="The grants list could not be read">
                  The grants could not be fetched, so
                  this table is empty because nothing was read — not because nothing has been
                  granted. The two tiles fed from this list read an em dash for the same reason.
                  Reload to try the read again.
                </EmptyState>
              ) : (
                <EmptyState icon="gift" title="No bonuses granted">
                  bonus grants has no rows. Nothing has been
                  granted against any promotion — a real zero, read live.
                </EmptyState>
              )
            }
          />
        </div>
      </Dialog>

      {/* ── New promotion wizard ───────────────────────────────────────── */}
      <Dialog
        open={wizardStep > 0}
        onClose={() => setWizardStep(0)}
        title={`New promotion — Step ${wizardStep} of 4 · ${WIZARD_STEPS[wizardStep - 1] ?? ''}`}
        wide
        actions={
          <>
            <Button variant="ghost" onClick={() => setWizardStep(0)}>
              Cancel
            </Button>
            {wizardStep > 1 && (
              <Button onClick={() => setWizardStep((s) => Math.max(1, s - 1))}>Back</Button>
            )}
            {wizardStep < 4 ? (
              <Button
                variant="primary"
                disabled={promoNextDisabled}
                onClick={() => setWizardStep((s) => Math.min(4, s + 1))}
              >
                Continue
              </Button>
            ) : (
              /* The wizard writes a DRAFT, and the button says so rather than
                 promising a state it does not reach. Publishing is a separate
                 decision taken against the stored version — which is what makes
                 "a promotion whose wagering is unset is not publishable" a rule
                 the platform enforces rather than a form that hid the button. */
              <Button
                variant="primary"
                disabled={!canPublish || saving}
                busy={saving}
                onClick={() => void submitWizard()}
              >
                {saving ? 'Saving…' : 'Save as draft'}
              </Button>
            )}
          </>
        }
      >
        <div className="stack">
          <ol className="row" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {WIZARD_STEPS.map((label, i) => (
              <li
                key={label}
                className="row"
                style={{ gap: 'var(--space-2)' }}
                // The current step is announced, not just tinted: the numbered
                // dot is colour, and colour on its own is not a position.
                aria-current={i + 1 === wizardStep ? 'step' : undefined}
              >
                <Tag tone={i + 1 === wizardStep ? 'accent' : i + 1 < wizardStep ? 'neutral' : 'quiet'}>
                  {i + 1}
                </Tag>
                <span className={i + 1 === wizardStep ? undefined : 'text-subtle'}>{label}</span>
              </li>
            ))}
          </ol>

          {wizardStep === 1 && (
            <div className="stack">
              <Field
                label="Promotion name (shown to players)"
                error={fields['promoName'] ?? fields['name']}
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    value={draft.promoName}
                    onChange={(e) => setDraft((d) => ({ ...d, promoName: e.target.value }))}
                    placeholder="e.g. September reload 50%"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
              {/* THIS HINT NAMED A COLUMN AND FOUR OF ITS VALUES. promotion.kind
                  holds DEPOSIT_MATCH · CASHBACK · FREE_SPINS · NO_DEPOSIT, and the
                  hint printed all five identifiers under a dropdown written in
                  plain English. The part that matters to the person choosing is
                  the substitution, not where it is kept: pick Fixed bonus or
                  Referral and the campaign is saved — and will appear on its card
                  — as a No deposit promotion. That label is something they can go
                  and check afterwards, which the enum name is not. The seed files
                  both of them the same way; that is a fact about the fixtures
                  rather than about their campaign, so it stays in this comment. */}
              <Field
                label="Bonus type"
                hint="The platform keeps four bonus types: Deposit match, Cashback, Free spins and No deposit. Fixed bonus and Referral are not types of their own — either one is saved as a No deposit promotion, and the review step says so before anything is written."
              >
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    value={draft.promoType}
                    onChange={(e) => setDraft((d) => ({ ...d, promoType: e.target.value }))}
                    aria-describedby={describedBy}
                  >
                    {['Deposit match', 'Cashback', 'Free spins', 'Fixed bonus', 'Referral'].map(
                      (t) => (
                        <option key={t}>{t}</option>
                      ),
                    )}
                  </Select>
                )}
              </Field>
              <div className="halves">
                <Field label="Match percentage" hint="Not stored as a field of its own — it would go into the eligibility rules.">
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      inputMode="numeric"
                      value={draft.matchPct}
                      onChange={(e) => setDraft((d) => ({ ...d, matchPct: e.target.value }))}
                      placeholder="50"
                      aria-describedby={describedBy}
                    />
                  )}
                </Field>
                <Field
                  label="Maximum bonus (PHP)"
                  hint="The most a single bonus from this promotion can be worth. Blank means no cap."
                  error={fields['maxBonus']}
                >
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      inputMode="decimal"
                      value={draft.maxBonus}
                      onChange={(e) => setDraft((d) => ({ ...d, maxBonus: e.target.value }))}
                      placeholder="5,000"
                      aria-describedby={describedBy}
                    />
                  )}
                </Field>
              </div>
              <Field label="Minimum deposit (PHP)" hint="Not stored as a field of its own — it would go into the eligibility rules.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    value={draft.minDeposit}
                    onChange={(e) => setDraft((d) => ({ ...d, minDeposit: e.target.value }))}
                    placeholder="500"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="stack">
              <Field label="Player segment">
                {({ id }) => (
                  <Select
                    id={id}
                    value={draft.segment}
                    onChange={(e) => setDraft((d) => ({ ...d, segment: e.target.value }))}
                  >
                    {[
                      'New depositors (no prior deposit)',
                      'All active players',
                      'VIP 3 and above',
                      'Dormant 30+ days',
                      'Custom segment…',
                    ].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </Select>
                )}
              </Field>
              <div className="halves">
                <Field
                  label="Brands"
                  hint="Which brand this promotion belongs to. “All brands” leaves it unset, which is how every existing promotion is filed. The card cannot show the brand afterwards — the list does not carry it."
                >
                  {({ id, describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={draft.brands}
                      onChange={(e) => setDraft((d) => ({ ...d, brands: e.target.value }))}
                    >
                      {/* "All 3" was written whatever the reader's brands, and a
                          made-up 3 when they were not known (module walk, 30 Oct 2026). */}
                      <option value={ALL_BRANDS}>All brands</option>
                      {brands.map((b) => (
                        <option key={b.id}>{b.name}</option>
                      ))}
                    </Select>
                  )}
                </Field>
                {/* The hint named the two values stored in the eligibility rules,
                    OPT_IN and AUTO. The operator chooses between the same two
                    things in words directly below it, so the identifiers told
                    them nothing they could act on. */}
                <Field label="Opt-in" hint="Recorded in the eligibility rules as Opt-in or Automatic.">
                  {({ id, describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={draft.optIn}
                      onChange={(e) => setDraft((d) => ({ ...d, optIn: e.target.value }))}
                    >
                      <option>Opt-in</option>
                      <option>Automatic</option>
                    </Select>
                  )}
                </Field>
              </div>
              <Field label="Excluded agents">
                {({ id }) => (
                  <Input
                    id={id}
                    value={draft.excludedAgents}
                    onChange={(e) => setDraft((d) => ({ ...d, excludedAgents: e.target.value }))}
                    placeholder="None"
                  />
                )}
              </Field>
              <div className="notice notice-quiet">
                <Icon name="users-three" />
                <div>
                  Opt-in decides whether a player is OFFERED this promotion on their rewards
                  screen; every eligibility rule the platform can actually check is re-run on the
                  server at the moment a bonus is granted, so a player who qualified when the
                  screen was drawn can still be refused — with the reason, not a blank. An
                  automatic promotion is never offered to opt into at all: it is granted from the
                  console, because nothing on this platform computes a deposit match or a cashback
                  share. No estimated reach is shown: nothing counts how many players a promotion
                  could reach, so no number is shown rather than a made-up one.
                </div>
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="stack">
              <div className="halves">
                <Field
                  label="Wagering multiplier (× bonus)"
                  error={fields['promoWager'] ?? fields['wageringMultiplier']}
                  hint={
                    draft.promoType === 'Free spins'
                      ? 'Free spins multiply WINNINGS, not the bonus amount.'
                      : 'Stored in basis points: 20× is 2000. Left blank the draft is saved without terms — and a version with no terms cannot be published.'
                  }
                >
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      inputMode="numeric"
                      value={draft.promoWager}
                      onChange={(e) => setDraft((d) => ({ ...d, promoWager: e.target.value }))}
                      placeholder="20"
                      aria-describedby={describedBy}
                    />
                  )}
                </Field>
                <Field label="Completion window (days)" hint="Not stored anywhere — the platform has no field for it.">
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      inputMode="numeric"
                      value={draft.windowDays}
                      onChange={(e) => setDraft((d) => ({ ...d, windowDays: e.target.value }))}
                      placeholder="14"
                      aria-describedby={describedBy}
                    />
                  )}
                </Field>
              </div>
              <Field label="Game weighting" hint="Not stored anywhere — the platform has no field for it.">
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    value={draft.weighting}
                    onChange={(e) => setDraft((d) => ({ ...d, weighting: e.target.value }))}
                    aria-describedby={describedBy}
                  >
                    {[
                      'Slots 100% · Live casino 10% · Table 5%',
                      'Slots 100% only',
                      'All games 100%',
                    ].map((w) => (
                      <option key={w}>{w}</option>
                    ))}
                  </Select>
                )}
              </Field>
              <div className="halves">
                <Field label="Max bet while wagering (PHP)" hint="Not stored anywhere — the platform has no field for it.">
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      inputMode="decimal"
                      value={draft.maxBet}
                      onChange={(e) => setDraft((d) => ({ ...d, maxBet: e.target.value }))}
                      placeholder="200"
                      aria-describedby={describedBy}
                    />
                  )}
                </Field>
                <Field label="Max win from bonus (PHP)" hint="Not stored anywhere — the platform has no field for it.">
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      inputMode="decimal"
                      value={draft.maxWin}
                      onChange={(e) => setDraft((d) => ({ ...d, maxWin: e.target.value }))}
                      placeholder="50,000"
                      aria-describedby={describedBy}
                    />
                  )}
                </Field>
              </div>
              <div className="notice notice-quiet">
                <Icon name="arrow-u-left-up" />
                <div>Forfeited bonus returns to promotion budget, not to the player.</div>
              </div>
            </div>
          )}

          {wizardStep === 4 && (
            <div className="stack">
              <dl className="stack">
                <div className="def-row">
                  <dt>Name</dt>
                  <dd>{draft.promoName.trim() || 'Untitled promotion'}</dd>
                </div>
                <div className="def-row">
                  <dt>Type</dt>
                  <dd>
                    {draft.promoType}
                    {/* NOT "stored as NO_DEPOSIT". The value is real and so is the
                        substitution, but what this person can verify afterwards is
                        the label on the card, which is No deposit — so that is the
                        word the review step uses. */}
                    {WIZARD_KIND[draft.promoType] === 'NO_DEPOSIT' &&
                    draft.promoType !== 'No deposit' ? (
                      <>
                        {' '}
                        <span className="text-subtle">
                          — saved as a No deposit promotion, the nearest type this
                          platform has
                        </span>
                      </>
                    ) : null}
                  </dd>
                </div>
                <div className="def-row">
                  <dt>Eligibility</dt>
                  <dd>
                    {draft.segment} · {draft.brands} · {draft.optIn}
                  </dd>
                </div>
                <div className="def-row">
                  <dt>Wagering</dt>
                  <dd>
                    {draft.promoWager || '—'}×{' '}
                    {draft.promoType === 'Free spins' ? 'winnings' : 'bonus'}
                    {draft.windowDays ? `, ${draft.windowDays}-day window` : ''}
                  </dd>
                </div>
                <div className="def-row">
                  <dt>Activation</dt>
                  <dd className="text-subtle">
                    saved as a draft — publish it from its card to set the window
                  </dd>
                </div>
                <div className="def-row">
                  <dt>Forecast cost</dt>
                  <dd className="text-subtle">no segment-size source</dd>
                </div>
              </dl>

              <Field
                label="Why this campaign"
                error={fields['changeNote'] ?? fields['note']}
                hint="At least 10 characters. This is the only place the reason for a version is kept — it goes into the audit trail, because the promotion itself has no field for it."
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={draft.changeNote}
                    onChange={(e) => setDraft((d) => ({ ...d, changeNote: e.target.value }))}
                    placeholder="Reload offer for the September payday weekend."
                  />
                )}
              </Field>

              <div className="notice">
                <Icon name="seal-check" />
                <div>
                  This writes <strong>version 1 as a draft</strong> and requires{' '}
                  somebody allowed to publish —{' '}
                  saving it as a draft. Nothing is live
                  until the draft is published from its card, and a version whose wagering
                  multiplier is still unset is refused at that point rather than here. A published promotion is never edited
                  in place: later changes are duplicated forward as v2.
                </div>
              </div>

              <div className="notice notice-quiet">
                <Icon name="warning-circle" />
                <div>
                  <strong>Seven fields on this form are not stored.</strong> Match percentage, minimum
                  deposit, completion window, game weighting, max bet while wagering, max win and
                  excluded agents have no place on{' '}
                  the promotion record and are not among the eligibility
                  rules the cards read, so they are dropped rather than written somewhere nothing
                  will ever look. What IS written is the name, the type, the wagering multiplier,
                  the maximum bonus, the opt-in mode and the brand.
                  {canPublish ? null : (
                    <>
                      {' '}
                      Saving is also disabled for your role ({session?.role ?? 'unknown'}), which
                      cannot publish a promotion — only a Super Admin can, and a second factor is
                      required to do it.
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* ── Deciding on one version ─────────────────────────────────────────
          One dialog for all four moves, because the shape of the decision is
          the same every time: which version, what it does to grants already
          held, a reason CODE from the closed set the route parses, and a note
          long enough to be worth reading. */}
      <Dialog
        open={acting !== null}
        onClose={() => setActing(null)}
        title={
          acting
            ? acting.action === 'duplicate'
              ? `Duplicate ${acting.row.key} v${acting.row.version} as a draft?`
              : `${PRIMARY_LABEL[acting.action]} — ${acting.row.key} v${acting.row.version}?`
            : 'Promotion'
        }
        actions={
          <>
            <Button onClick={() => setActing(null)}>Cancel</Button>
            <Button
              variant="primary"
              busy={saving}
              disabled={saving || note.trim().length < 10}
              onClick={() => void submitAction()}
            >
              {saving ? 'Recording…' : acting ? ACTION_LABEL[acting.action] : 'Confirm'}
            </Button>
          </>
        }
      >
        {acting && (
          <div className="stack">
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                {acting.action === 'duplicate' ? (
                  <>
                    This writes the NEXT version of {acting.row.key} as a draft, carrying{' '}
                    {wageringLabel(acting.row)} and{' '}
                    {acting.row.max_bonus === null
                      ? 'no cap'
                      : `a ${formatPesos(BigInt(acting.row.max_bonus))} cap`}{' '}
                    forward. Nothing is live and nothing changes on v{acting.row.version} — a
                    published promotion is never edited in place.
                  </>
                ) : acting.action === 'publish' ? (
                  <>
                    Publishing v{acting.row.version} retires whichever version is in force and
                    changes nothing on it, so a player already holding a bonus keeps the terms they
                    were granted under. A start still ahead schedules it; otherwise it goes live
                    now. A version whose wagering multiplier is still unset is refused.
                  </>
                ) : acting.action === 'pause' ? (
                  <>
                    Pausing stops NEW grants. The{' '}
                    {formatCount(Number(acting.row.players))}{' '}
                    {Number(acting.row.players) === 1 ? 'player' : 'players'} already granted a
                    bonus against this version keep it, and it keeps running to its own expiry —
                    nothing in bonus grants is touched.
                  </>
                ) : acting.action === 'cancel' ? (
                  <>
                    Calling off a schedule that has not opened. v{acting.row.version} moves to{' '}
                    <Tag tone="quiet">Cancelled</Tag> and will never grant anything — it is not a
                    pause and there is no resuming from it. Nothing is clawed back, because a
                    scheduled promotion has granted nothing; the count is read as the status changes
                    and recorded, so the one case that contradicts that is visible rather than
                    silent.{' '}
                    {acting.row.starts_at ? (
                      <>
                        It was due to open on{' '}
                        <strong>{manilaDay(acting.row.starts_at)}</strong>.
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    Resuming starts new grants again. Nothing that expired while it was paused
                    comes back: a grant&rsquo;s window ran out on its own clock, and this is a
                    decision about the campaign rather than about any player&rsquo;s bonus.
                  </>
                )}
              </div>
            </div>

            {acting.action === 'publish' && (
              <>
                <div className="halves">
                  <Field
                    label="Opens on"
                    error={fields['startsOn']}
                    hint="Leave blank to go live the moment you publish."
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        type="date"
                        value={startsOn}
                        onChange={(e) => setStartsOn(e.target.value)}
                        aria-describedby={describedBy}
                        aria-invalid={invalid || undefined}
                      />
                    )}
                  </Field>
                  <Field
                    label="Closes after"
                    error={fields['endsOn']}
                    hint="Inclusive — the promotion runs to the end of this day."
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        type="date"
                        value={endsOn}
                        onChange={(e) => setEndsOn(e.target.value)}
                        aria-describedby={describedBy}
                        aria-invalid={invalid || undefined}
                      />
                    )}
                  </Field>
                </div>
                <div className="notice notice-quiet">
                  <Icon name="info" />
                  <div>
                    Both days are read on the <strong>gaming day</strong> — Asia/Manila midnight,
                    16:00 UTC — so a promotion opens and closes on the same boundary every figure
                    measured against it uses. A start in the future publishes it as{' '}
                    <Tag tone="neutral">Scheduled</Tag> rather than live; nothing grants until it
                    opens.{' '}
                    {startsOn || endsOn ? (
                      <>
                        This will run{' '}
                        <span className="tabular">
                          {startsOn ? `from ${startsOn}` : 'from the moment you publish'}
                          {endsOn ? ` to the end of ${endsOn}` : ', with no closing date'}
                        </span>
                        .
                      </>
                    ) : (
                      <>
                        Leaving both blank publishes it <strong>live and open-ended</strong> — it
                        will keep granting until somebody pauses it. That was the only thing this
                        console could do before these two fields existed; it is still allowed, but
                        it is now a choice rather than the default nobody made.
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {acting.action !== 'duplicate' && (
              <Field
                label="Reason"
                error={fields['reasonCode']}
                hint="A closed set, so a report can count lifecycle moves by cause. The sentence below rides with it in the audit trail."
              >
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}
                  >
                    {reasonOptions.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}

            <Field
              label="Note"
              error={fields['note']}
              hint="At least 10 characters. A code on its own reads as a form number; the note is what the next operator reads."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={
                    acting.action === 'pause'
                      ? 'Bonus budget for September is spent.'
                      : acting.action === 'resume'
                        ? 'September budget topped up — new grants can start again.'
                        : 'Wagering corrected from 25× to 20× after the terms review.'
                  }
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>

      {/* ── Granting a bonus ────────────────────────────────────────────────
          The only dialog on this screen that moves money. Everything it states
          about what will happen is what the route actually does: the amount
          leaves PROMO_FUNDING, lands in the player's bonus wallet, and is
          locked until the wagering is cleared. */}
      <Dialog
        open={granting !== null}
        onClose={() => setGranting(null)}
        title={granting ? `Grant a bonus — ${granting.key} v${granting.version}` : 'Grant a bonus'}
        actions={
          <>
            <Button onClick={() => setGranting(null)}>Cancel</Button>
            <Button
              variant="primary"
              busy={saving}
              disabled={saving || note.trim().length < 10 || grantPlayerRef.trim() === ''}
              onClick={() => void submitGrant()}
            >
              {saving ? 'Granting…' : 'Grant bonus'}
            </Button>
          </>
        }
      >
        {granting && (
          <div className="stack">
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                {/* THE TWO ACCOUNT NAMES ARE GONE. PROMO_FUNDING and PLAYER_BONUS
                    are kinds on wallet.account, and the console already has its
                    own words for them — "Promotion budget" and "Player bonus",
                    from ACCOUNT_KIND_LABEL in the transactions feature, which is
                    what the ledger screens print. Somebody about to hand a player
                    a bonus needs to know the money leaves the budget and lands
                    somewhere unspendable; the names of the two rows that move are
                    no use to them. */}
                The amount is debited from this brand&rsquo;s promotion budget and credited to the
                player&rsquo;s bonus wallet — a balanced
                ledger movement written in the same transaction as the grant and its audit row.{' '}
                <strong>It is not cash.</strong> It converts to spendable balance only when the
                wagering requirement is met, and returns to the budget if it is forfeited or
                expires.{' '}
                {granting.kind === 'FREE_SPINS' ? (
                  <>
                    This is a <strong>free spins</strong> promotion, so the{' '}
                    {wageringLabel(granting)} requirement is worked out from the winnings the spins
                    produce — not from the amount below, and not until they are played.
                  </>
                ) : (
                  <>
                    The requirement will be {wageringLabel(granting)}: the multiplier applies to
                    the amount below.
                  </>
                )}
              </div>
            </div>

            <Field
              label="Player reference"
              error={fields['playerRef']}
              hint="The reference on the player record, e.g. PL-10241. It is looked up in your own brand scope before anything is granted."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={grantPlayerRef}
                  onChange={(e) => setGrantPlayerRef(e.target.value)}
                  placeholder="PL-10241"
                />
              )}
            </Field>

            <div className="halves">
              <Field
                label="Amount (PHP)"
                error={fields['amount']}
                hint={
                  granting.max_bonus === null
                    ? 'This version publishes no cap, so there is no ceiling the platform can enforce.'
                    : `Capped at ${formatPesos(BigInt(granting.max_bonus))} by the published version.`
                }
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    placeholder="500"
                  />
                )}
              </Field>
              <Field
                label="Expires in (days)"
                error={fields['expiry']}
                hint={
                  granting.ends_at
                    ? `Blank uses the promotion's own end, ${manilaDay(granting.ends_at)}.`
                    : 'Blank means the bonus never expires — this version has no end date of its own.'
                }
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    value={grantExpiryDays}
                    onChange={(e) => setGrantExpiryDays(e.target.value)}
                    placeholder="30"
                  />
                )}
              </Field>
            </div>

            <Field
              label="Reason"
              error={fields['reasonCode']}
              hint="A closed set, so bonus spend can be counted by cause. The note rides with it in the audit trail."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                >
                  {GRANT_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Note"
              error={fields['note']}
              hint="At least 10 characters. This is what a finance review reads when it asks why the money left the budget."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Qualified for the September reload; agreed with the VIP desk."
                />
              )}
            </Field>

            <div className="notice notice-quiet">
              <Icon name="shield-warning" />
              <div>
                Every rule is re-checked on the server before anything is written: that the version
                is live and inside its window, that the player is on the right brand, that they are
                not under a <strong>bonus exclusion</strong> or self-excluded, that the account is
                not suspended, and that they are not already holding an active grant from this
                version. A refusal writes nothing at all — no grant, no ledger movement.
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* ── Taking one back ───────────────────────────────────────────────
        HELD OPEN. An operator reaches this dialog from one row in the grants
        table, having already decided that this particular bonus is coming
        back, and the note they then have to write is the only account anybody
        will ever have of why — the player is shown no reason at all. A stray
        Escape against that half-typed note loses the decision silently and
        drops them back on a table where every row still looks the same, so the
        likeliest outcome is that the forfeit quietly does not happen.

        It is deliberately NOT `dismissible={!saving}`. Blocking Escape only
        while the request is in flight guards the single moment at which the
        decision has already been taken and the server is doing the work; the
        moment that needs the guard is the one before the press. Cancel sits in
        the actions row, names itself, and leaves by the same door.
      */}
      <Dialog
        open={forfeiting !== null}
        onClose={() => setForfeiting(null)}
        title={forfeiting ? `Forfeit ${forfeiting.ref}?` : 'Forfeit bonus'}
        dismissible={false}
        actions={
          <>
            <Button onClick={() => setForfeiting(null)}>Cancel</Button>
            <Button
              variant="primary"
              busy={saving}
              disabled={saving || note.trim().length < 10}
              onClick={() => void submitForfeit()}
            >
              {saving ? 'Forfeiting…' : 'Forfeit bonus'}
            </Button>
          </>
        }
      >
        {forfeiting && (
          <div className="stack">
            {/*
              DANGER, not warn, and not the quiet tone it wore until now. Two of
              the three things the tone contract reserves danger for happen on
              this press at once: money moves — the bonus wallet is debited and
              the amount is posted back to the promotion's funding pot, which is
              a real reversal with its own reference — and a reported figure
              moves with it, because a campaign's cost is granted minus
              forfeited and this lowers it the moment it is written. It is also
              irreversible; there is no un-forfeit anywhere in the console, and
              the server refuses a second attempt on the same grant.

              The wording is the paragraph that was already here, unchanged. It
              has been right since before there was a route to go with it, and
              the one sentence added to it is the thing the operator cannot see
              from this screen: who finds out, and how little they are told.
              Nothing is sent to the player on this path — no mail, no message —
              and their rewards page lists the bonus as Removed with the
              turnover they cleared and no cause beside it. That is why the note
              below is worth writing properly: it is the entire answer support
              has when the player rings to ask where the money went.
            */}
            <div className="notice notice-danger">
              <Icon name="warning" />
              <div>
                {formatPesos(BigInt(forfeiting.amount))} goes back to the promotion budget —{' '}
                <strong>not to {forfeiting.player}</strong>, and not to the house. The turnover
                already put through (
                {formatPesos(BigInt(forfeiting.wagering_completed))} of{' '}
                {formatPesos(BigInt(forfeiting.wagering_required))}) is not refunded and does not
                count towards anything else. Nothing is emailed: the player sees the bonus listed
                as Removed on their rewards page, with no reason given, so the note below is the
                only answer support will have if they ring. This cannot be undone; granting again
                is a new grant.
              </div>
            </div>

            <Field
              label="Reason"
              error={fields['reasonCode']}
              hint="A closed set, so forfeits can be counted by cause."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={forfeitReason}
                  onChange={(e) => setForfeitReason(e.target.value)}
                >
                  {FORFEIT_REASONS.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Note"
              error={fields['note']}
              hint="At least 10 characters. A player who rings to ask where their bonus went is answered from this."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Multiple accounts on one device confirmed by the risk review."
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>
    </>
  );
}

/* ── The card grid ─────────────────────────────────────────────────────────
   The design is explicit that this module is a card console rather than a
   table: the unit of work is a campaign, not a row, and the four figures on a
   card are read together. Each card is a real button so it is reachable from
   the keyboard without any extra wiring. */

function PromotionGrid({
  rows,
  onOpen,
}: {
  rows: PromotionRow[];
  onOpen: (key: string) => void;
}) {
  return (
    <>
      {rows.map((p) => {
        const cost = BigInt(p.cost);
        return (
          <button
            key={p.id}
            type="button"
            className="card elev-sm"
            onClick={() => onOpen(p.key)}
            style={{ border: 0, font: 'inherit', color: 'inherit', textAlign: 'start', cursor: 'pointer' }}
          >
            <span className="row">
              <span className="card-kicker">{p.key}</span>
              <span className="header-spacer">
                <Tag tone={STATUS_TONE[p.status] ?? 'neutral'}>{statusLabel(p.status)}</Tag>
              </span>
            </span>
            <span className="card-title">{p.name}</span>
            <span className="card-meta">
              {kindLabel(p.kind)} · {windowLabel(p)} · {optInLabel(p)}
            </span>
            {/* The design puts a rule between what the campaign IS and what it
                has DONE, and the two halves read as one block without it. A
                <span> rather than the design's <div>: this card is a <button>,
                whose content model is phrasing content only. */}
            <span className="hr" role="presentation" />
            <span
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-2)',
              }}
            >
              <CardMetric label="Players" value={formatCount(Number(p.players))} />
              {/* "—" when nothing has been granted: a scheduled promotion has
                  cost nothing yet, and ₱0.00 would read as a measured result. */}
              <CardMetric
                label="Bonus cost"
                value={cost === 0n ? '—' : formatPesosCompact(cost)}
              />
              <CardMetric label="Wagering" value={wageringLabel(p)} />
              <CardMetric label="Effect" value="not measured" muted />
            </span>
          </button>
        );
      })}
    </>
  );
}

function CardMetric({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <span style={{ display: 'block', minWidth: 0 }}>
      <span className="stat-label" style={{ display: 'block' }}>
        {label}
      </span>
      <span className={muted ? 'text-subtle' : 'tabular'} style={{ fontSize: '13px' }}>
        {value}
      </span>
    </span>
  );
}
